import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Building2, Users, Phone, Mail, MessageSquare, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAM_SIZES = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-1000 employees',
  '1000+ employees',
];

const EnterpriseContactModal = ({ open, onOpenChange }) => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const isFr = i18n.language === 'fr';
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
    email: user?.email || '',
    company: user?.company_name || '',
    team_size: '',
    phone: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.team_size || !form.message) {
      toast.error(isFr ? 'Veuillez remplir tous les champs obligatoires.' : 'Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/enterprise`, { ...form, lang: i18n.language });
      setSubmitted(true);
    } catch (err) {
      toast.error(isFr ? 'Erreur d\'envoi. Réessayez.' : 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => setSubmitted(false), 400);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            {isFr ? 'Contacter notre équipe Enterprise' : 'Contact our Enterprise Team'}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            {isFr
              ? 'Découvrez comment ProposAI peut s\'adapter à vos besoins à grande échelle.'
              : 'Discover how ProposAI can scale to your needs with custom pricing and dedicated support.'}
          </p>
        </DialogHeader>

        {submitted ? (
          <div className="py-10 text-center" data-testid="enterprise-success-state">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {isFr ? 'Merci pour votre intérêt!' : "Thanks for reaching out!"}
            </h3>
            <p className="text-gray-500 text-sm mb-6">
              {isFr
                ? 'Notre équipe vous contactera dans les 24 heures.'
                : "Our team will get back to you within 24 hours."}
            </p>
            <Button onClick={handleClose} data-testid="enterprise-close-btn">
              {isFr ? 'Fermer' : 'Close'}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="enterprise-contact-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {isFr ? 'Nom complet' : 'Full Name'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder={isFr ? 'Jean Dupont' : 'John Smith'}
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="enterprise-name-input"
                  />
                  <Users className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {isFr ? 'Email professionnel' : 'Work Email'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="enterprise-email-input"
                  />
                  <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {isFr ? 'Entreprise' : 'Company'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder={isFr ? 'Nom de l\'entreprise' : 'Company name'}
                    value={form.company}
                    onChange={e => setForm({ ...form, company: e.target.value })}
                    required
                    data-testid="enterprise-company-input"
                  />
                  <Building2 className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {isFr ? 'Taille de l\'équipe' : 'Team Size'} <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
                  value={form.team_size}
                  onChange={e => setForm({ ...form, team_size: e.target.value })}
                  required
                  data-testid="enterprise-team-size-select"
                >
                  <option value="">{isFr ? 'Sélectionner...' : 'Select...'}</option>
                  {TEAM_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {isFr ? 'Téléphone (optionnel)' : 'Phone (optional)'}
              </label>
              <div className="relative">
                <input
                  type="tel"
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="+1 (555) 000-0000"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  data-testid="enterprise-phone-input"
                />
                <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {isFr ? 'Message' : 'How can we help?'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <textarea
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                  rows={3}
                  placeholder={isFr
                    ? 'Décrivez vos besoins en matière d\'AO et ce que vous recherchez...'
                    : 'Tell us about your RFP volume, team needs, and what you\'re looking for...'}
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  required
                  data-testid="enterprise-message-input"
                />
                <MessageSquare className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
              </div>
            </div>

            {/* Enterprise features reminder */}
            <div className="bg-secondary border border-primary/20 rounded-lg p-3">
              <p className="text-xs font-semibold text-primary mb-1.5">{isFr ? 'Inclus dans Enterprise:' : 'Included in Enterprise:'}</p>
              <div className="grid grid-cols-2 gap-1">
                {(isFr ? [
                  'AOs illimités',
                  'Flux personnalisés',
                  'Marque blanche',
                  'SLA garanti',
                  'Gestionnaire dédié',
                  'Onboarding complet'
                ] : [
                  'Unlimited RFPs',
                  'Custom workflows',
                  'White-label',
                  'SLA guarantees',
                  'Dedicated manager',
                  'Full onboarding'
                ]).map(f => (
                  <div key={f} className="flex items-center gap-1.5 text-xs text-primary">
                    <CheckCircle2 className="w-3 h-3 text-primary shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full"
              size="lg"
              data-testid="enterprise-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isFr ? 'Envoi en cours...' : 'Sending...'}</>
              ) : (
                isFr ? 'Envoyer ma demande' : 'Send Inquiry'
              )}
            </Button>

            <p className="text-xs text-gray-400 text-center">
              {isFr ? 'Nous vous répondrons dans les 24 heures.' : "We'll get back to you within 24 hours."}
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnterpriseContactModal;
